import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { hue } from "../lib/time";
import { hueOf } from "../lib/api";

const escStack = [];
export function useEscape(onClose) {
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    const me = {};
    escStack.push(me);
    const k = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented || escStack[escStack.length - 1] !== me) return;
      e.preventDefault();
      cb.current();
    };
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("keydown", k); escStack.splice(escStack.indexOf(me), 1); };
  }, []);
}

const layers = [];
const FOC = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const focusables = (el) => (el ? [...el.querySelectorAll(FOC)].filter((x) => x.getClientRects().length && !x.closest("[inert]")) : []);
function syncInert() {
  const top = layers[layers.length - 1];
  document.getElementById("root")?.toggleAttribute("inert", layers.length > 0);
  layers.forEach((l) => l.current?.parentElement?.toggleAttribute("inert", l !== top));
}

function useDialog(onClose) {
  const panel = useRef(null);
  const opener = useRef(document.activeElement);
  useEscape(onClose);
  useEffect(() => {
    const o = opener.current;
    layers.push(panel);
    syncInert();
    const p = panel.current;
    if (p && !p.contains(document.activeElement)) p.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key !== "Tab" || layers[layers.length - 1] !== panel || !panel.current) return;
      const items = [...focusables(panel.current), ...focusables(document.getElementById("ck-toasts"))];
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && (i === -1 || i === items.length - 1)) { e.preventDefault(); items[0].focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      layers.splice(layers.indexOf(panel), 1);
      syncInert();
      setTimeout(() => {
        let t = o && o !== document.body && o.isConnected ? o : document.querySelector("main h1");
        if (!t || t.closest("[inert]")) return;
        if (!t.matches(FOC) && !t.hasAttribute("tabindex")) t.setAttribute("tabindex", "-1");
        t.focus({ preventScroll: true });
      });
    };
  }, []);
  return panel;
}

export function ClientChip({ code, name, label }) {
  const h = hueOf(code) ?? hue(code);
  const chip = <span className="chip" style={{ background: `oklch(0.5 0.13 ${h})` }} title={name} aria-hidden={label ? "true" : undefined}>{code}</span>;
  return label ? <span className="chip-l">{chip}<span className="chip-n">{name || code}</span></span> : chip;
}

const STATE = { waiting_client: "Waiting on client", waiting_internal: "Waiting on team", on_hold: "On hold", not_given: "Not given",
  paused: "Paused", doing: "Running", done: "Done" };

export function StateTag({ t }) {
  if (t.kind === "standby") return <span className="stag">If any</span>;
  return STATE[t.state] ? <span className={`stag st-${t.state}`}>{STATE[t.state]}</span> : null;
}

export function Sheet({ title, onClose, children, testid, width = 460 }) {
  const panel = useDialog(onClose);
  const tid = useId();
  return createPortal(
    <div className="sheet-bg" onClick={onClose}>
      <aside ref={panel} tabIndex={-1} className="sheet card-hard" role="dialog" aria-labelledby={tid} data-sheet style={{ "--w": `${width}px` }}
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="sheet-head">
          <h2 id={tid}>{title}</h2>
          <button className="x" aria-label="Close" onClick={onClose} data-testid={`${testid}-close`}>×</button>
        </div>
        {children}
      </aside>
    </div>, document.body);
}

export function Chips({ items, onPick, testid }) {
  return (
    <div className="chips">
      {items.map(([v, l]) => (
        <button key={v} className="chip-btn" onClick={() => onPick(v)} data-testid={`${testid}-${v}`}>{l}</button>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} aria-disabled={disabled || undefined}
      className={`sw-t hit ${checked ? "on" : ""}`} onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!checked); }} data-testid={testid}>
      <span aria-hidden="true" />
    </button>
  );
}

export const Kbd = ({ k, dark }) => <kbd className={`kbd ${dark ? "dark" : ""}`} aria-hidden="true">{k}</kbd>;
